/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `set` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class SetMesg : Mesg {
    public constructor() : super("Set", Profile.MesgNum.SET)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp. Timestamp of the set */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** duration, in s. */
    public var duration: Double?
        get() = getFieldValue(DURATION_FIELD_NUM) as? Double
        set(value) = setFieldValue(DURATION_FIELD_NUM, value)

    /** repetitions. # of repitions of the movement */
    public var repetitions: UShort?
        get() = getFieldValue(REPETITIONS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REPETITIONS_FIELD_NUM, value)

    /** weight, in kg. Amount of weight applied for the set */
    public var weight: Double?
        get() = getFieldValue(WEIGHT_FIELD_NUM) as? Double
        set(value) = setFieldValue(WEIGHT_FIELD_NUM, value)

    /** set_type. */
    public var setType: SetType?
        get() = (getFieldValue(SET_TYPE_FIELD_NUM) as? UByte)?.let { SetType.fromValue(it) }
        set(value) = setFieldValue(SET_TYPE_FIELD_NUM, value?.value)

    /** start_time. Start time of the set */
    public var startTime: Instant?
        get() = (getFieldValue(START_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** Number of values stored in category. */
    public val numCategory: Int
        get() = getField(CATEGORY_FIELD_NUM)?.numValues ?: 0

    /** category. */
    public var category: List<ExerciseCategory?>?
        get() {
            val field = getField(CATEGORY_FIELD_NUM) ?: return null
            return List(field.numValues) { getCategory(it) }
        }
        set(value) {
            removeField(CATEGORY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCategory(index, element) }
        }

    public fun getCategory(index: Int): ExerciseCategory? =
        (getFieldValue(CATEGORY_FIELD_NUM, index) as? UShort)?.let { ExerciseCategory.fromValue(it) }

    public fun setCategory(index: Int, value: ExerciseCategory?) {
        setFieldValue(CATEGORY_FIELD_NUM, value?.value, index)
    }

    /** Number of values stored in category_subtype. */
    public val numCategorySubtype: Int
        get() = getField(CATEGORY_SUBTYPE_FIELD_NUM)?.numValues ?: 0

    /** category_subtype. Based on the associated category, see [category]_exercise_names */
    public var categorySubtype: List<UShort?>?
        get() {
            val field = getField(CATEGORY_SUBTYPE_FIELD_NUM) ?: return null
            return List(field.numValues) { getCategorySubtype(it) }
        }
        set(value) {
            removeField(CATEGORY_SUBTYPE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCategorySubtype(index, element) }
        }

    public fun getCategorySubtype(index: Int): UShort? =
        getFieldValue(CATEGORY_SUBTYPE_FIELD_NUM, index) as? UShort

    public fun setCategorySubtype(index: Int, value: UShort?) {
        setFieldValue(CATEGORY_SUBTYPE_FIELD_NUM, value, index)
    }

    /** weight_display_unit. */
    public var weightDisplayUnit: FitBaseUnit?
        get() = (getFieldValue(WEIGHT_DISPLAY_UNIT_FIELD_NUM) as? UShort)?.let { FitBaseUnit.fromValue(it) }
        set(value) = setFieldValue(WEIGHT_DISPLAY_UNIT_FIELD_NUM, value?.value)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** wkt_step_index. */
    public var wktStepIndex: UShort?
        get() = getFieldValue(WKT_STEP_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(WKT_STEP_INDEX_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 254u
        public const val DURATION_FIELD_NUM: UByte = 0u
        public const val REPETITIONS_FIELD_NUM: UByte = 3u
        public const val WEIGHT_FIELD_NUM: UByte = 4u
        public const val SET_TYPE_FIELD_NUM: UByte = 5u
        public const val START_TIME_FIELD_NUM: UByte = 6u
        public const val CATEGORY_FIELD_NUM: UByte = 7u
        public const val CATEGORY_SUBTYPE_FIELD_NUM: UByte = 8u
        public const val WEIGHT_DISPLAY_UNIT_FIELD_NUM: UByte = 9u
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 10u
        public const val WKT_STEP_INDEX_FIELD_NUM: UByte = 11u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("Timestamp", 254u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("Duration", 0u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[3] = Field("Repetitions", 3u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[4] = Field("Weight", 4u, BaseType.UINT16, 16.0, 0.0, "kg", false)
            fields[5] = Field("SetType", 5u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[6] = Field("StartTime", 6u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[7] = Field("Category", 7u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[8] = Field("CategorySubtype", 8u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[9] = Field("WeightDisplayUnit", 9u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[10] = Field("MessageIndex", 10u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[11] = Field("WktStepIndex", 11u, BaseType.UINT16, 1.0, 0.0, "", false)
            return fields
        }
    }
}
